import type { WidgetInitOptions } from '../protocol';

/**
 * The tiny public script (spec §13/§34): `window.DigishareWebChat.init(...)`.
 * Runs in the HOST page. Draws the launcher bubble itself (a Shadow DOM host,
 * not an iframe — isolates it from host CSS with near-zero cost) and only
 * creates the real chat iframe — the actual widget bundle — on first
 * interaction (click) or after a short idle timeout, so a Web Chat outage or
 * slow load never blocks the host site (spec §34's lazy-load pattern).
 *
 * postMessage is used only for UI orchestration (open/close/unread-count)
 * between this script and the widget iframe — never for the session token or
 * any API call, which stay entirely inside the iframe's own JS context. Both
 * sides still validate `event.source` against the exact window reference they
 * created/were given (spec §32) — the real cross-origin gate for anything
 * that matters is the backend's per-channel allowed_domains check (task 03)
 * on every actual API request, not this channel.
 */

declare const __WEBCHAT_API_BASE_URL__: string;

const PROTOCOL_TAG = 'digishare-webchat';
/** Matches phones in portrait/landscape and small windows where full-screen is optimal. */
const MOBILE_QUERY = window.matchMedia('(max-width: 640px), (max-height: 500px)');
const IDLE_LOAD_MS = 4000;
const LOADER_SCRIPT_SRC = (document.currentScript as HTMLScriptElement | null)?.src ?? null;
const DEFAULT_WIDGET_URL = resolveDefaultWidgetUrl();

type Command = 'open' | 'close' | 'reset' | 'identify' | 'bootstrap';

interface LoaderMessage {
  source: typeof PROTOCOL_TAG;
  type: 'command';
  command: Command;
  payload?: unknown;
}

interface WidgetMessage {
  source: typeof PROTOCOL_TAG;
  type: 'event';
  event: 'ready' | 'unread-count' | 'close-request' | 'bootstrap-request';
  payload?: unknown;
}

function resolveDefaultWidgetUrl(): string {
  if (LOADER_SCRIPT_SRC) {
    try {
      return new URL('widget.html', LOADER_SCRIPT_SRC).toString();
    } catch {
      /* fall through */
    }
  }
  return '/webchat/widget.html';
}

/**
 * Where the loader's own bootstrap request goes. The build-time
 * WEBCHAT_API_BASE_URL (see build.mjs) is either absolute, or the default
 * '/v1' — which for the widget means "same origin as widget.html", so here it
 * is resolved against this script's own src rather than the host page (the
 * client's site has no /v1). An explicit init({ apiBaseUrl }) wins: it's the
 * host page's own code choosing where its own request goes, and no session
 * token is involved at this stage (unlike the widget's stricter rule).
 */
function resolveApiBaseUrl(override?: string): string {
  const configured = override || __WEBCHAT_API_BASE_URL__ || '/v1';
  try {
    return new URL(configured, LOADER_SCRIPT_SRC ?? location.href).toString().replace(/\/+$/, '');
  } catch {
    return configured.replace(/\/+$/, '');
  }
}

class DigishareWebChatController {
  private options: Required<Pick<WidgetInitOptions, 'channelId'>> & WidgetInitOptions;
  private root: HTMLDivElement | null = null;
  private shadow: ShadowRoot | null = null;
  private bubble: HTMLButtonElement | null = null;
  private badge: HTMLSpanElement | null = null;
  private frame: HTMLIFrameElement | null = null;
  private opened = false;
  private widgetLoaded = false;
  private idleTimer: number | null = null;
  private pendingIdentify: string | null = null;
  private viewportTracking = false;

  constructor(options: WidgetInitOptions) {
    if (!options.channelId) {
      throw new Error('DigishareWebChat.init() requires a channelId.');
    }
    this.options = { position: 'right', locale: 'fr', ...options };
    this.mount();
    window.addEventListener('message', this.onMessage);
  }

  private mount(): void {
    const isLeft = this.options.position === 'left';
    const side = isLeft ? 'left' : 'right';

    const root = document.createElement('div');
    root.setAttribute('data-digishare-webchat', '');
    root.style.position = 'fixed';
    root.style.zIndex = '2147483000';
    root.style[side] = '20px';
    root.style[side] = `max(16px, env(safe-area-inset-${side}))`;
    root.style.bottom = '20px';
    root.style.bottom = 'max(16px, env(safe-area-inset-bottom))';
    document.body.appendChild(root);
    this.root = root;

    const shadow = root.attachShadow({ mode: 'open' });
    this.shadow = shadow;

    const style = document.createElement('style');
    style.textContent = `
      :host { all: initial; }
      .bubble {
        width: 60px; height: 60px; border-radius: 50%; border: none;
        cursor: pointer; box-shadow: 0 6px 18px rgba(0,0,0,.2);
        background: var(--dwc-color, #4f46e5); color: #fff;
        display: flex; align-items: center; justify-content: center;
        font: 600 24px/1 system-ui, sans-serif; position: relative;
        transition: transform .2s ease, box-shadow .2s ease;
      }
      .bubble:hover {
        transform: scale(1.05);
        box-shadow: 0 8px 24px rgba(0,0,0,.25);
      }
      .bubble svg { width: 28px; height: 28px; }
      .badge {
        position: absolute; top: -4px; right: -4px; min-width: 18px; height: 18px;
        border-radius: 9px; background: #ef4444; color: #fff; font: 600 11px/18px system-ui;
        text-align: center; padding: 0 4px; display: none;
      }
      iframe {
        border: none; position: fixed;
        bottom: 20px;
        bottom: max(16px, env(safe-area-inset-bottom));
        width: 380px; max-width: calc(100vw - 32px); max-width: calc(100dvw - 32px);
        height: 600px; max-height: calc(100vh - 40px); max-height: calc(100dvh - 40px);
        border-radius: 18px;
        box-shadow: 0 18px 50px rgba(20,22,28,.18), 0 0 0 1px rgba(20,22,28,.06);
        display: none; background: #fff;
        z-index: 2147483000;
      }
      iframe.frame--left {
        left: 20px;
        left: max(16px, env(safe-area-inset-left));
        right: auto;
      }
      iframe.frame--right {
        right: 20px;
        right: max(16px, env(safe-area-inset-right));
        left: auto;
      }
      @media (max-width: 640px), (max-height: 500px) {
        .bubble {
          width: 54px; height: 54px;
        }
        .bubble svg { width: 26px; height: 26px; }
        iframe, iframe.frame--left, iframe.frame--right {
          position: fixed; inset: 0; top: 0; left: 0; right: 0; bottom: 0;
          width: 100vw; width: 100dvw;
          height: 100vh; height: 100dvh;
          max-height: none; max-width: none;
          border-radius: 0;
          box-shadow: none;
        }
      }
    `;
    shadow.appendChild(style);

    const bubble = document.createElement('button');
    bubble.className = 'bubble';
    bubble.setAttribute('aria-label', 'Open chat');
    bubble.innerHTML = chatIconSvg();
    const badge = document.createElement('span');
    badge.className = 'badge';
    bubble.appendChild(badge);
    bubble.addEventListener('click', () => this.toggle());
    shadow.appendChild(bubble);
    this.bubble = bubble;
    this.badge = badge;

    this.scheduleIdleLoad();
  }

  private scheduleIdleLoad(): void {
    this.idleTimer = window.setTimeout(() => this.ensureWidgetLoaded(), IDLE_LOAD_MS);
  }

  private ensureWidgetLoaded(): void {
    if (this.widgetLoaded || !this.shadow) return;
    if (this.idleTimer) {
      window.clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    this.widgetLoaded = true;

    const params = new URLSearchParams({
      channelId: this.options.channelId,
      locale: this.options.locale ?? 'fr',
      position: this.options.position ?? 'right',
    });
    if (this.options.apiBaseUrl) params.set('apiBaseUrl', this.options.apiBaseUrl);

    const frame = document.createElement('iframe');
    frame.title = 'Digishare Web Chat';
    frame.src = `${this.options.widgetUrl ?? DEFAULT_WIDGET_URL}?${params.toString()}`;
    frame.classList.add(this.options.position === 'left' ? 'frame--left' : 'frame--right');
    frame.allow = 'microphone; geolocation';
    this.shadow.appendChild(frame);
    this.frame = frame;
  }

  private onMessage = (event: MessageEvent): void => {
    if (!this.frame || event.source !== this.frame.contentWindow) return;
    const data = event.data as WidgetMessage | undefined;
    if (!data || data.source !== PROTOCOL_TAG || data.type !== 'event') return;

    if (data.event === 'ready') {
      if (this.pendingIdentify) {
        this.postToWidget({ source: PROTOCOL_TAG, type: 'command', command: 'identify', payload: this.pendingIdentify });
        this.pendingIdentify = null;
      }
      if (this.opened) this.postToWidget({ source: PROTOCOL_TAG, type: 'command', command: 'open' });
    } else if (data.event === 'bootstrap-request') {
      const requestId = String((data.payload as { requestId?: unknown } | undefined)?.requestId ?? '');
      if (!requestId) return;
      void this.fetchBootstrapCode().then((code) => {
        this.postToWidget({ source: PROTOCOL_TAG, type: 'command', command: 'bootstrap', payload: { requestId, code } });
      });
    } else if (data.event === 'unread-count') {
      const count = Number(data.payload) || 0;
      if (this.badge) {
        this.badge.textContent = count > 9 ? '9+' : String(count);
        this.badge.style.display = count > 0 ? 'block' : 'none';
      }
    } else if (data.event === 'close-request') {
      this.close();
    }
  };

  /**
   * The one network request this script makes itself. It runs on the host
   * page, so its Origin header is the embedding site — browser-enforced, the
   * only signal the backend can check allowed_domains against (every request
   * the widget makes comes from inside the iframe, whose origin is just
   * widget.html's host). The code it returns is single-use and short-lived;
   * the widget exchanges it for its session, so the session token itself
   * never exists in this page's JS context. Null on any failure — the widget
   * then tries without one and surfaces the backend's rejection.
   */
  private async fetchBootstrapCode(): Promise<string | null> {
    try {
      const res = await fetch(`${resolveApiBaseUrl(this.options.apiBaseUrl)}/webchat/session/bootstrap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ channelId: this.options.channelId }),
      });
      if (!res.ok) return null;
      const body = (await res.json()) as { bootstrapCode?: unknown };
      return typeof body.bootstrapCode === 'string' && body.bootstrapCode ? body.bootstrapCode : null;
    } catch {
      return null;
    }
  }

  private postToWidget(message: LoaderMessage): void {
    if (!this.frame?.contentWindow) return;
    // Always the widget's actual origin, never '*': the bootstrap code rides
    // this channel, and DEFAULT_WIDGET_URL is exactly where the frame was
    // pointed, so a resolved origin is always available.
    const targetOrigin = new URL(this.options.widgetUrl ?? DEFAULT_WIDGET_URL, location.href).origin;
    this.frame.contentWindow.postMessage(message, targetOrigin);
  }

  open(): void {
    this.opened = true;
    this.ensureWidgetLoaded();
    if (this.frame) this.frame.style.display = 'block';
    if (this.bubble) this.bubble.style.display = 'none';
    this.startViewportTracking();
    this.postToWidget({ source: PROTOCOL_TAG, type: 'command', command: 'open' });
  }

  close(): void {
    this.opened = false;
    this.stopViewportTracking();
    if (this.frame) this.frame.style.display = 'none';
    if (this.bubble) this.bubble.style.display = 'flex';
    this.postToWidget({ source: PROTOCOL_TAG, type: 'command', command: 'close' });
  }

  /**
   * On phones the widget is full screen, and the on-screen keyboard shrinks
   * only the *visual* viewport (Chrome's default `interactive-widget=
   * resizes-visual`): the iframe keeps its full height and the composer ends
   * up behind the keyboard. The iframe can't know (its own visualViewport is
   * just its box), so the loader — on the top-level page — pins it to the
   * visual viewport's height/offset while open. No-op on desktop widths.
   */
  private startViewportTracking(): void {
    const vv = window.visualViewport;
    if (!vv || this.viewportTracking) return;
    this.viewportTracking = true;
    vv.addEventListener('resize', this.fitToViewport);
    vv.addEventListener('scroll', this.fitToViewport);
    window.addEventListener('resize', this.fitToViewport);
    MOBILE_QUERY.addEventListener('change', this.fitToViewport);
    this.fitToViewport();
  }

  private stopViewportTracking(): void {
    const vv = window.visualViewport;
    if (!this.viewportTracking) return;
    this.viewportTracking = false;
    vv?.removeEventListener('resize', this.fitToViewport);
    vv?.removeEventListener('scroll', this.fitToViewport);
    window.removeEventListener('resize', this.fitToViewport);
    MOBILE_QUERY.removeEventListener('change', this.fitToViewport);
    if (this.frame) {
      this.frame.style.height = '';
      this.frame.style.top = '';
      this.frame.style.width = '';
      this.frame.style.left = '';
    }
  }

  private fitToViewport = (): void => {
    const vv = window.visualViewport;
    if (!this.frame || !vv) return;
    if (!MOBILE_QUERY.matches) {
      this.frame.style.height = '';
      this.frame.style.top = '';
      this.frame.style.width = '';
      this.frame.style.left = '';
      return;
    }
    this.frame.style.height = `${Math.round(vv.height)}px`;
    this.frame.style.top = `${Math.round(vv.offsetTop)}px`;
    this.frame.style.width = `${Math.round(vv.width)}px`;
    this.frame.style.left = `${Math.round(vv.offsetLeft)}px`;
  };

  toggle(): void {
    if (this.opened) this.close();
    else this.open();
  }

  show(): void {
    if (this.root) this.root.style.display = 'block';
  }

  hide(): void {
    if (this.root) this.root.style.display = 'none';
  }

  reset(): void {
    this.ensureWidgetLoaded();
    this.postToWidget({ source: PROTOCOL_TAG, type: 'command', command: 'reset' });
  }

  identify(visitorId: string): void {
    if (this.widgetLoaded) {
      this.postToWidget({ source: PROTOCOL_TAG, type: 'command', command: 'identify', payload: visitorId });
    } else {
      this.pendingIdentify = visitorId;
    }
  }
}

function chatIconSvg(): string {
  return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>';
}

let instance: DigishareWebChatController | null = null;

const api = {
  init(options: WidgetInitOptions): void {
    if (instance) return;
    instance = new DigishareWebChatController(options);
  },
  open(): void {
    instance?.open();
  },
  close(): void {
    instance?.close();
  },
  toggle(): void {
    instance?.toggle();
  },
  show(): void {
    instance?.show();
  },
  hide(): void {
    instance?.hide();
  },
  reset(): void {
    instance?.reset();
  },
  identify(visitorId: string): void {
    instance?.identify(visitorId);
  },
};

declare global {
  interface Window {
    DigishareWebChat: typeof api;
  }
}

window.DigishareWebChat = api;
